import { PermissionProps, UserInfoProps } from '@app/types';

/**
 *
 * @param userPermissions user permissions
 * @param key key to check permissions
 * @returns whethe user has permissions
 */
const checkPermissions = (userPermissions: PermissionProps[], key: String) => {
  const permissions: PermissionProps[] = userPermissions.filter(
    (permission: PermissionProps) => {
      return permission.key === key;
    },
  );
  if (permissions && permissions.length) {
    return permissions[0].children;
  }
  return false;
};

/**
 *
 * @param userPermissions user permissions
 * @param key key to check permissions
 * @returns whethe user has permissions
 */
const checkTabPermission = (
  userPermissions: PermissionProps[],
  key: String,
) => {
  const permissions: PermissionProps[] = userPermissions.filter(
    (permission: PermissionProps) => {
      return permission.key === key;
    },
  );
  if (permissions && permissions.length) {
    return permissions[0].selectedValue;
  }
  return '';
};

/**
 *
 * @param permissions user  permissions
 * @param key key to check permission exists
 * @returns whethe user has child permissions
 */
const checkManageTransaction = (
  permissions: PermissionProps[],
  key: string,
) => {
  const manageTransactions: PermissionProps[] = permissions.filter(
    (childPermission: PermissionProps) => {
      return childPermission.key === key;
    },
  );
  if (manageTransactions && manageTransactions.length) {
    return manageTransactions[0].selectedKey;
  }
  return '';
};

/**
 *
 * @param userInfo logged in user information
 * @returns whether the user has access to transaction page or not
 */
export function showTransactionPage(userInfo: UserInfoProps) {
  if (userInfo) {
    const { permissions } = userInfo;
    if (permissions && permissions.length) {
      const transactionPermissions = checkTabPermission(
        permissions,
        'transactions_k',
      );
      return transactionPermissions === 1 ? true : false;
    }
    return false;
  } else {
    return false;
  }
}

/**
 *
 * @param userInfo user information
 * @returns whether user has permission to create transaction
 */
export function createTransaction(userInfo: UserInfoProps) {
  if (userInfo) {
    const { permissions } = userInfo;
    if (permissions && permissions.length) {
      const transactionPermissions = checkPermissions(
        permissions,
        'transactions_k',
      );
      if (transactionPermissions && transactionPermissions.length) {
        const createTransactionPermission: PermissionProps[] = transactionPermissions.filter(
          (childPermission: PermissionProps) => {
            return childPermission.key === 'create_transaction_k';
          },
        );
        if (createTransactionPermission && createTransactionPermission.length) {
          return createTransactionPermission[0].selectedValue === 1
            ? true
            : false;
        }
        return false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

/**
 *
 * @param userInfo user information
 * @returns whether user has permission to edit transaction
 */
export function editTransaction(
  userInfo: UserInfoProps,
  transactionId: string,
) {
  if (userInfo) {
    const { permissions } = userInfo;
    if (permissions && permissions.length) {
      const transactionPermissions = checkPermissions(
        permissions,
        'transactions_k',
      );
      if (transactionPermissions && transactionPermissions.length) {
        const editTransactionPermission = checkManageTransaction(
          transactionPermissions,
          'manage_transactions_k',
        );
        return transactionId &&
          editTransactionPermission &&
          (editTransactionPermission === 'view_and_edit_all_transactions' ||
            editTransactionPermission ===
              'view_and_edit_all_transactions_assigned_to_them_personally')
          ? true
          : false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

/**
 *
 * @param userInfo user information
 * @returns whether user has permission to edit transaction tasks
 */
export function checkEditTransactionPermission(userInfo: UserInfoProps) {
  const { permissions } = userInfo;
  if (permissions && permissions.length) {
    const transactionPermissions = checkPermissions(
      permissions,
      'transactions_k',
    );
    if (transactionPermissions && transactionPermissions.length) {
      const editTaskTransaction = checkManageTransaction(
        transactionPermissions,
        'manage_transaction_tasks_k',
      );
      return editTaskTransaction &&
        (editTaskTransaction === 'view_and_edit_assign_transaction_tasks' ||
          editTaskTransaction ===
            'view_and_edit_transaction_tasks_that_are_assigned_to_them_personally' ||
          editTaskTransaction === 'view_and_edit_all_transaction_tasks' ||
          editTaskTransaction ===
            'view_and_edit_transaction_tasks_that_are_assigned_to_them')
        ? true
        : false;
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export function checkEditTaskPermission(
  userInfo: UserInfoProps,
  selectedUser: any,
) {
  let selectedUserId: string = '';
  if (selectedUser && selectedUser.value) {
    selectedUserId = selectedUser.value || '';
  }
  const { permissions } = userInfo;
  if (permissions && permissions.length) {
    const transactionPermissions = checkPermissions(
      permissions,
      'transactions_k',
    );
    if (transactionPermissions && transactionPermissions.length) {
      const editTaskTransaction = checkManageTransaction(
        transactionPermissions,
        'manage_transaction_tasks_k',
      );
      return editTaskTransaction &&
        ((editTaskTransaction ===
          'view_and_edit_transaction_tasks_that_are_assigned_to_them_personally' &&
          selectedUserId === userInfo.firebaseId) ||
          editTaskTransaction === 'view_and_edit_all_transaction_tasks')
        ? true
        : false;
    } else {
      return false;
    }
  } else {
    return false;
  }
}

/**
 *
 * @param userInfo information of user
 * @returns whether user have permission to approve transaction
 */
export function viewTransactionTasks(userInfo: UserInfoProps) {
  if (userInfo) {
    const { permissions } = userInfo;
    if (permissions && permissions.length) {
      const transactionPermissions = checkPermissions(
        permissions,
        'transactions_k',
      );
      if (transactionPermissions && transactionPermissions.length) {
        const editTaskTransaction = checkManageTransaction(
          transactionPermissions,
          'manage_transaction_tasks_k',
        );
        return editTaskTransaction &&
          editTaskTransaction === 'view_assign_transaction_tasks'
          ? true
          : false;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }
}

/**
 *
 * @param userInfo information of user
 * @returns whether user have permission to approve transaction
 */
export function approveTransaction(userInfo: UserInfoProps, approved: boolean) {
  if (userInfo) {
    const { permissions } = userInfo;
    if (permissions && permissions.length) {
      const transactionPermissions = checkPermissions(
        permissions,
        'transactions_k',
      );
      if (transactionPermissions && transactionPermissions.length) {
        const approvePermission = checkManageTransaction(
          transactionPermissions,
          approved ? 'undo_transaction_approval_k' : 'approve_transaction_k',
        );
        return approvePermission && approvePermission === 'Yes' ? true : false;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }
}

/**
 *
 * @param userInfo user information
 * @returns whether user has municipality permission or not
 */
export function municipalityPermission(userInfo: UserInfoProps) {
  if (userInfo) {
    const { permissions } = userInfo;
    if (permissions && permissions.length) {
      const municipalityPermissions = checkTabPermission(
        permissions,
        'municipality_k',
      );
      return municipalityPermissions && municipalityPermissions === 1
        ? true
        : false;
    } else {
      return false;
    }
  }
}

/**
 *
 * @param userInfo user information
 * @returns whether user has permssion to create municipality
 */
export function createMunicipality(userInfo: UserInfoProps) {
  if (userInfo) {
    const { permissions } = userInfo;
    if (permissions && permissions.length) {
      const municipalityPermissions = checkPermissions(
        permissions,
        'municipality_k',
      );
      if (municipalityPermissions && municipalityPermissions.length) {
        const createMunicipalityPermission = checkManageTransaction(
          municipalityPermissions,
          'create_municipality_k',
        );
        return createMunicipalityPermission &&
          createMunicipalityPermission === 'Yes'
          ? true
          : false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

/**
 *
 * @param userInfo user information
 * @returns whether user has permssion to create municipality
 */
export function editMunicipality(userInfo: UserInfoProps) {
  if (userInfo) {
    const { permissions } = userInfo;
    if (permissions && permissions.length) {
      const municipalityPermissions = checkPermissions(
        permissions,
        'municipality_k',
      );
      if (municipalityPermissions && municipalityPermissions.length) {
        const editMunicipalityPermission = checkManageTransaction(
          municipalityPermissions,
          'manage_municipalities_k',
        );
        return editMunicipalityPermission &&
          (editMunicipalityPermission === 'view_and_edit_all_municipalities' ||
            editMunicipalityPermission ===
              'view_and_edit_their_municipality_information')
          ? true
          : false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

/**
 *
 * @param userInfo user information
 * @returns whether user has permssion to user roles
 */
export function usersPermissions(userInfo: UserInfoProps) {
  if (userInfo) {
    const { permissions } = userInfo;
    if (permissions && permissions.length) {
      const userRoleAccess = checkTabPermission(permissions, 'users_k');
      return userRoleAccess && userRoleAccess === 1 ? true : false;
    } else {
      return false;
    }
  } else {
    return false;
  }
}

/**
 *
 * @param userInfo user information
 * @returns whether user has permission to see cost items in payment due section
 */
export function checkCostItemsPermission(userInfo: UserInfoProps) {
  if (userInfo && userInfo.roleData) {
    const { roleData } = userInfo;
    return roleData.key &&
      roleData.key !== 'realtor_professional_user' &&
      roleData.key !== 'titleAgent_professional_user'
      ? true
      : false;
  }
  return false;
}
